import { useMutation } from '@tanstack/react-query';

import {
    FidelityAccountList,
    FidelityApplicationSection,
    FidelityDownloadButton,
    FidelityDownloadFeedback,
    FidelityPacketCard,
} from '@/components/fidelity/FidelityApplicationComponents';
import { CustomerApi } from 'API';

import { accountTitle } from './account-model';
import { FidelityBusinessApplicationView } from './FidelityBusinessApplicationView';
import { FidelityPersonalApplicationView } from './FidelityPersonalApplicationView';
import { FidelityRetirementApplicationView } from './FidelityRetirementApplicationView';
import { FidelityTransferStatus, useTransferAction } from './FidelityTransferApplicationView';
import { FidelityTrustApplicationView } from './FidelityTrustApplicationView';
import { FidelitySmaSectionProps, useFidelitySmaApplications } from './useFidelitySmaApplications';
import { OnboardingHousehold } from './useOnboardingHousehold';

export const FidelityApplicationView = ({ household }: { household: Pick<OnboardingHousehold, 'accounts'> }) => {
    const sma = useFidelitySmaApplications(household);
    const transfer = useTransferAction(household, sma.ssnAccess);
    const queries = [sma.personal, sma.trust, sma.business, sma.retirement, sma.ira];
    // Only successful section results render account rows. Failed sections leave transfers accessible below.
    const grouped = new Set([
        ...(sma.personal.isSuccess ? (sma.personal.data?.applications.flat() ?? []) : []),
        ...[sma.trust, sma.business, sma.retirement].flatMap((query) =>
            query.isSuccess ? (query.data?.applications.flatMap((packet) => packet.accountGUIDs) ?? []) : [],
        ),
        ...(sma.ira.isSuccess ? (sma.ira.data?.flat() ?? []) : []),
    ]);
    const transferAccounts = new Set([
        ...(transfer.applications.data?.accountGUIDs ?? []),
        ...(transfer.applications.data?.excludedAccounts.map((notice) => notice.accountGUID) ?? []),
    ]);
    const ungrouped = household.accounts
        .map(({ account }) => account.accountGUID)
        .filter((guid) => transferAccounts.has(guid) && !grouped.has(guid));
    const sectionProps = {
        household,
        action: transfer.action,
        ssnAccess: sma.ssnAccess,
        checkingAccess: sma.checkingAccess,
    };
    if (household.accounts.length === 0) return null;
    if (sma.checkingAccess) return <p role="status">Checking access…</p>;
    if (!sma.ssnAccess) return <p role="status">SSN access is required to generate an application.</p>;
    return (
        <div className="flex flex-col gap-10">
            <FidelityTransferStatus applications={transfer.applications} />
            <FidelityPersonalApplicationView {...sectionProps} applications={sma.personal} />
            <FidelityTrustApplicationView {...sectionProps} applications={sma.trust} />
            <FidelityBusinessApplicationView {...sectionProps} applications={sma.business} />
            <FidelityRetirementApplicationView {...sectionProps} applications={sma.retirement} />
            <FidelityIraApplicationView {...sectionProps} applications={sma.ira} />
            {transfer.applications.isSuccess && queries.every((query) => !query.isPending) && ungrouped.length > 0 && (
                <section aria-label="Other transfer documents" className="flex flex-col gap-5">
                    <h2 className="font-heading text-xl">Other transfer documents</h2>
                    <FidelityAccountList
                        accountGUIDs={ungrouped}
                        action={transfer.action}
                        title={(guid) => {
                            const account = household.accounts.find((row) => row.account.accountGUID === guid)!.account;
                            return accountTitle(account);
                        }}
                    />
                </section>
            )}
        </div>
    );
};

const FidelityIraApplicationView = ({
    household,
    applications,
    action,
    ssnAccess,
    checkingAccess,
}: FidelitySmaSectionProps<'ira'>) => {
    const accountGUIDs = household.accounts.map(({ account }) => account.accountGUID);

    return (
        <FidelityApplicationSection
            name="IRA"
            hasAccounts={accountGUIDs.length > 0}
            groupCount={applications.data?.length}
            checkingAccess={checkingAccess}
            ssnAccess={!!ssnAccess}
            applications={applications}
        >
            <>
                {applications.data?.map((accounts) => (
                    <SmaIraApplication
                        key={accounts.join(',')}
                        accountGUIDs={accounts}
                        household={household}
                        action={action}
                        onRefresh={() => void applications.refetch()}
                    />
                ))}
            </>
        </FidelityApplicationSection>
    );
};

const SmaIraApplication = ({
    accountGUIDs,
    household,
    onRefresh,
    action,
}: {
    accountGUIDs: string[];
    onRefresh: () => void;
    action: FidelitySmaSectionProps<'ira'>['action'];
    household: Pick<OnboardingHousehold, 'accounts'>;
}) => {
    const generate = useMutation({
        // The shared API client downloads attachment responses directly; do not cache the PDF.
        mutationFn: () => CustomerApi({ hideError: true }).getSmaIraForm({ accountGUIDs }),
    });
    return (
        <FidelityPacketCard
            name="IRA"
            accountCount={accountGUIDs.length}
            action={
                <FidelityDownloadButton download={generate} variant="outline" onClick={() => generate.mutate()}>
                    Generate SMA IRA Application
                </FidelityDownloadButton>
            }
        >
            <FidelityAccountList
                action={action}
                accountGUIDs={accountGUIDs}
                title={(guid) => {
                    const account = household.accounts.find((row) => row.account.accountGUID === guid)?.account;
                    return account ? accountTitle(account) : 'Account no longer available';
                }}
            >
                {(guid) => {
                    const label = household.accounts.find((row) => row.account.accountGUID === guid)?.account
                        .contraLabel;
                    return label ? <p className="text-cocoa-600 text-sm">From {label}</p> : null;
                }}
            </FidelityAccountList>
            <FidelityDownloadFeedback
                download={generate}
                onRefresh={() => {
                    generate.reset();
                    onRefresh();
                }}
            />
        </FidelityPacketCard>
    );
};
